import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import useAuth from "./useAuth";
import * as XLSX from "xlsx";
import Header from "./Header";
import Sidebar from "./Sidebar";
import Footer from "./Footer";
import jsPDF from "jspdf";
import Pagination from "./Pagination";
import "./Manage_User.css";
import "./Loader.css";
import { FaFilePdf } from "react-icons/fa";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const Loader = () => (
  <div className="loader-container1">
    <div className="spinner1"></div>
    <p>Loading...</p>
  </div>
);

const ManageFinanceConveyance = () => {
  useAuth();
  const navigate = useNavigate();

  const [EmpCode, setEmpCode] = useState("");
  const [items, setItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // ✅ Fetch profile first
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) return;

        const res = await fetch(`${API_BASE_URL}profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const result = await res.json();
        if (result.status && result.profile?.emp_code) {
          setEmpCode(result.profile.emp_code);
        }
      } catch (err) {
        console.error("Profile fetch error:", err);
      }
    };
    fetchProfile();
  }, []);

  // ✅ Fetch conveyance data AFTER EmpCode is ready
  useEffect(() => {
    const fetchItems = async () => {
      if (!EmpCode) return;
      setLoading(true);
      try {
        const token = localStorage.getItem("authToken");
        const res = await fetch(`${API_BASE_URL}getfinancemasterdetails`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const result = await res.json();

        if (result.status && result.data) {
          // Wrap single object in array
          setItems(result.data);
          setError("");
        } else {
          setItems([]);
          setError("No records found");
        }
      } catch (err) {
        console.error("Fetch conveyance error:", err);
        setItems([]);
        setError("Failed to fetch conveyance details");
      } finally {
        setLoading(false);
      }
    };

    fetchItems();
  }, [EmpCode]);

  // ✅ Filter items
  const filteredItems = items.filter((item) => {
    const q = searchQuery.toLowerCase();
    return (
      item.employee_name?.toLowerCase().includes(q) ||
      item.emp_code?.toLowerCase().includes(q) ||
      item.branch?.toLowerCase().includes(q) ||
      item.purpose?.toLowerCase().includes(q) ||
      item.remarks?.toLowerCase().includes(q)
    );
  });

  // ✅ Pagination logic
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);

  const startItem = filteredItems.length === 0 ? 0 : indexOfFirstItem + 1;
  const endItem = Math.min(indexOfLastItem, filteredItems.length);

  // ✅ Export to Excel
  const exportToExcel = () => {
    const data = filteredItems.map((item) => ({
      EmpCode: item.emp_code,
      Employee: item.employee_name,
      Branch: item.branch,
      Purpose: item.purpose,
      Amount: item.amount,
      Date: item.visit_date,
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Conveyance");
    XLSX.writeFile(wb, "CM_ZM_Conveyance.xlsx");
  };

  const formatDate = (d) => {
    if (!d) return "";
    const date = new Date(d);
    return date.toLocaleDateString("en-GB");
  };

  const downloadPDF = async (empCode) => {
    try {
      const token = localStorage.getItem("authToken");

      const res = await fetch(
        `${API_BASE_URL}getmyvisitdetails/${empCode}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const result = await res.json();

      if (!result.status || !result.data.length) {
        alert("No data found");
        return;
      }

      const visits = result.data;

      const doc = new jsPDF("p", "mm", "a4");

      // 🔹 Header
      doc.setFontSize(14);
      doc.text("ZM / CM Conveyance Details", 14, 15);

      doc.setFontSize(10);
      doc.text(`Employee Name : ${visits[0].employee_name}`, 14, 25);
      doc.text(`Employee Code : ${visits[0].emp_code}`, 14, 31);
      doc.text(`Role : ${visits[0].role}`, 14, 37);
      doc.text(`Duration : ${visits[0].duration_type} days`, 14, 43);

      // 🔹 Table
      doc.autoTable({
        startY: 50,
        head: [[
          "V",
          "Branch",
          "Date",
          "From Time",
          "To Time",
          "Day",
          "Purpose",
          "Remarks",
          "Amount"
        ]],
        body: visits.map((v, i) => [
          i + 1,
          v.branch,
          v.visit_date,
          v.visit_from_time,
          v.visit_to_time,
          v.visit_day,
          v.purpose,
          v.remarks,
          `₹${v.amount}`,
        ]),
        styles: {
          fontSize: 8,
          cellPadding: 2,
        },
        headStyles: {
          fillColor: [52, 152, 219], // same blue theme
          textColor: 255,
        },
      });

      // 🔹 Total
      const total = visits.reduce((sum, v) => sum + Number(v.amount), 0);
      // doc.text(
      //   `Total Amount : ₹${total.toFixed(2)}`,
      //   14,
      //   doc.lastAutoTable.finalY + 10
      // );

      doc.save(`Conveyance_${empCode}.pdf`);
    } catch (err) {
      console.error(err);
      alert("PDF download failed");
    }
  };

  return (
    <div className="hold-transition sidebar-mini layout-fixed" style={{ fontSize: '12px' }}>
      <div className="wrapper">
        <Header />
        <Sidebar />
        <div className="content-wrapper">
          <div className="content-header">
            <div className="container-fluid">
              <div className="row mb-2">
                <div className="col-sm-6">
                  <h4 className="m-0" style={{ fontSize: '20px' }}>
                    Finance Conveyance
                  </h4>
                </div>
                <div className="col-sm-6">
                  <ol className="breadcrumb float-sm-right">
                    <li className="breadcrumb-item">
                      <Link to="/dashboard">Home</Link>
                    </li>
                    <li className="breadcrumb-item active">Finance Conveyance</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
          <section className="content">
            <div className="container-fluid">
              <div className="card">


                <div className="card-body">
                  {loading ? (
                    <Loader />
                  ) : (
                    <>
                      <div style={{ float: "right", marginBottom: "10px" }}>
                        <input
                          type="search"
                          className="form-control form-control-sm"
                          placeholder="Search"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                        />
                      </div>

                      <div className="table-responsive">
                        <table className="table table-bordered table-striped">
                          <thead>
                            <tr>
                              <th>S.No</th>
                              <th>Emp Code</th>
                              <th>Name</th>
                              <th>Role</th>
                              <th>Month_Year</th>
                              <th>Total Visits</th>
                              <th>Conveyance Status</th>
                              <th>Audit Status</th>
                              <th>Finance Status</th>
                              {/* <th>Added Amount</th> */}
                              <th>Total Visit Amount</th>
                              {/* <th>Attachment</th> */}
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {currentItems.length > 0 ? (
                              currentItems.map((item, index) => (
                                <tr key={item.id || index}>
                                  <td>{index + 1 + (currentPage - 1) * itemsPerPage}</td>
                                  <td>{item.emp_code}</td>
                                  <td>{item.comp_name}</td>
                                  <td>{item.role}</td>
                                  <td>{item.month_year}</td>
                                  {/* <td>{item.total_visits_30}</td> */}
                                  <td>
                                    {item.role === "cluster_manager"
                                      ? item.total_visits_30 * 2
                                      : item.total_visits_30}
                                  </td>

                                  <td>{item.conveyance_status}</td>
                                  <td
                                    style={{
                                      fontWeight: "600",
                                      color:
                                        item.audit_status === "Approved"
                                          ? "green"
                                          : item.audit_status === "Pending"
                                            ? "orange"
                                            : item.audit_status === "Rejected"
                                              ? "red"
                                              : ''
                                    }}
                                  >{item.audit_status}</td>
                                  <td
                                    style={{
                                      fontWeight: "600",
                                      color:
                                        item.finance_status === "Approved"
                                          ? "green"
                                          : item.finance_status === "Pending"
                                            ? "orange"
                                            : item.finance_status === "Rejected"
                                              ? "red"
                                              : ''
                                    }}
                                  >{item.finance_status}</td>
                                  {/* <td>₹{item.amount}</td> */}
                                  <td>₹{item.total_visit_amount}</td>
                                  {/* <td>
                                    {item.attachment ? (
                                      <a
                                        href={`${API_BASE_URL.replace("/api/", "")}assets/uploads/${item.attachment}`}
                                        target="_blank"
                                        rel="noreferrer"
                                      >
                                        View
                                      </a>
                                    ) : (
                                      "-"
                                    )}
                                  </td> */}
                                  <td>
                                    <Link to={`/edit_financecmzm_conveyance/${item.id}`}>
                                      <button
                                        className="btn btn-sm btn-primary"
                                        style={{ fontSize: "11px", padding: "3px 8px" }}
                                      >
                                        Edit
                                      </button>
                                    </Link>
                                    <button
                                      onClick={() => downloadPDF(item.emp_code)}
                                      title="Download PDF"
                                      style={{
                                        background: "transparent",
                                        border: "none",
                                        cursor: "pointer",
                                        padding: "4px",
                                      }}
                                    >
                                      <FaFilePdf
                                        size={18}
                                        color="#d32f2f"
                                        style={{ verticalAlign: "middle" }}
                                      />
                                    </button>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="9" align="center">
                                  {error || "No records found"}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                handlePageChange={setCurrentPage}
              />

              <div className="mb-3">
                Showing {startItem} to {endItem} of {filteredItems.length} entries
              </div>
            </div>
          </section>
        </div>
        <Footer />
      </div>
    </div>
  );
};

export default ManageFinanceConveyance;
